import type { AppState, SortOption } from "../state";
import type { ReadingStatus } from "../../models/types";
import { getSelectedBook } from "../state";
import { isSignedIn } from "../../services/storage";
import { esc } from "./stars";

const SORT_LABELS: Record<SortOption, string> = {
  dateRead: "Read",
  series: "Series",
  title: "Title",
};

const FILTER_OPTIONS: { value: ReadingStatus | null; label: string; shortLabel: string; color: string }[] = [
  { value: null, label: "All", shortLabel: "All", color: "orange" },
  { value: "reading", label: "Reading", shortLabel: "Reading", color: "blue" },
  { value: "want-to-read", label: "Want to Read", shortLabel: "Want", color: "gold" },
  { value: "read", label: "Read", shortLabel: "Read", color: "lavender" },
];

export function renderHeader(state: AppState): string {
  let breadcrumbText: string;
  let breadcrumbColor: string;

  switch (state.view) {
    case "add":
      breadcrumbText = "Add Book";
      breadcrumbColor = "violet";
      break;
    case "detail":
      breadcrumbText = `Edit: ${esc(getSelectedBook(state)?.title ?? "Book")}`;
      breadcrumbColor = "blue";
      break;
    case "notFound":
      breadcrumbText = "Not Found";
      breadcrumbColor = "red";
      break;
    default:
      breadcrumbText = "";
      breadcrumbColor = "lavender";
      break;
  }

  const sortButtons = (["dateRead", "series", "title"] as SortOption[])
    .map(
      (opt) =>
        `<button class="sort-btn sort-btn--${opt === state.sortOption ? "selected" : "deselected"}" data-sort="${opt}">${SORT_LABELS[opt]}</button>`,
    )
    .join("\n            ");

  return `
    <header class="lcars-header">
      <div class="lcars-header__elbow">
        <span class="lcars-header__elbow-label">LCARS ${__APP_VERSION__}</span>
      </div>
      <div class="lcars-header__bar">
        <div class="lcars-header__top-strip">
          <span class="lcars-header__breadcrumb-label lcars-header__breadcrumb-label--${breadcrumbColor}">
            ${breadcrumbText}
          </span>
          <span class="lcars-header__breadcrumb-fill"></span>
        </div>
        <div class="lcars-header__title-row">
          <div class="lcars-header__title-bar">
            <h1 class="lcars-header__title">Book List</h1>
          </div>
          <div class="sort-buttons sort-buttons--desktop">
            ${sortButtons}
          </div>
          <button class="sort-trigger sort-trigger--mobile" id="sort-trigger-btn">
            ${SORT_LABELS[state.sortOption]}
          </button>
        </div>
      </div>
    </header>
  `;
}

export function renderSortModal(currentSort: SortOption): string {
  const options = (["dateRead", "series", "title"] as SortOption[])
    .map(
      (opt) =>
        `<button class="sort-modal__option sort-modal__option--${opt === currentSort ? "selected" : "deselected"}" data-sort-modal="${opt}">${SORT_LABELS[opt]}</button>`,
    )
    .join("\n        ");

  return `
    <div class="sort-modal-overlay" id="sort-modal-overlay">
      <div class="sort-modal">
        <div class="sort-modal__title">Sort By</div>
        ${options}
      </div>
    </div>
  `;
}

export function renderFilterModal(state: AppState): string {
  const counts: Record<string, number> = {
    all: state.library.books.length,
    reading: state.library.books.filter((b) => b.status === "reading").length,
    "want-to-read": state.library.books.filter((b) => b.status === "want-to-read").length,
    read: state.library.books.filter((b) => b.status === "read").length,
  };

  const options = FILTER_OPTIONS.map(({ value, label, color }) => {
    const filterKey = value ?? "all";
    const isSelected = state.filterStatus === value;
    return `<button class="filter-modal__option filter-modal__option--${color} filter-modal__option--${isSelected ? "selected" : "deselected"}" data-filter-modal="${value ?? ""}">${label} (${counts[filterKey]})</button>`;
  }).join("\n        ");

  return `
    <div class="filter-modal-overlay" id="filter-modal-overlay">
      <div class="filter-modal">
        <div class="filter-modal__title">Filter</div>
        ${options}
      </div>
    </div>
  `;
}

export function getFilterTriggerLabel(state: AppState): string {
  const opt = FILTER_OPTIONS.find((o) => o.value === state.filterStatus);
  return opt?.shortLabel ?? "All";
}

export function renderNavBar(state: AppState): string {
  let buttons: string;

  switch (state.view) {
    case "add":
      buttons = renderAddNavButtons();
      break;
    case "detail":
      buttons = renderDetailNavButtons();
      break;
    default:
      buttons = renderListNavButtons(state);
      break;
  }

  return `
    <nav class="lcars-nav">
      ${buttons}
      <div class="lcars-nav__spacer"></div>
      <div class="lcars-nav__bottom"></div>
    </nav>
  `;
}

export function renderMobileSearchBar(state: AppState): string {
  if (state.view !== "list") return "";
  return `
    <div class="lcars-mobile-search">
      <div class="lcars-search-wrapper">
        <input class="lcars-mobile-search__input lcars-input" id="mobile-search-input"
               placeholder="Search..." value="${esc(state.searchQuery)}" />
        ${state.searchQuery ? `<button class="lcars-search-clear" id="mobile-search-clear-btn" type="button" aria-label="Clear search">&times;</button>` : ""}
      </div>
    </div>
  `;
}

function renderListNavButtons(state: AppState): string {
  const active = (status: string | null) =>
    state.filterStatus === status ? " lcars-nav__block--active" : "";

  const bookCount = state.library.books.length;
  const readingCount = state.library.books.filter(
    (b) => b.status === "reading",
  ).length;
  const wantCount = state.library.books.filter(
    (b) => b.status === "want-to-read",
  ).length;
  const readCount = state.library.books.filter(
    (b) => b.status === "read",
  ).length;

  const filterTriggerLabel = getFilterTriggerLabel(state);

  return `
    <button class="lcars-nav__block lcars-nav__block--orange${active(null)}" data-filter="">
      All (${bookCount})
    </button>
    <button class="lcars-nav__block lcars-nav__block--blue${active("reading")}" data-filter="reading">
      Reading (${readingCount})
    </button>
    <button class="lcars-nav__block lcars-nav__block--gold${active("want-to-read")}" data-filter="want-to-read">
      Want to Read (${wantCount})
    </button>
    <button class="lcars-nav__block lcars-nav__block--lavender${active("read")}" data-filter="read">
      Read (${readCount})
    </button>
    <button class="filter-trigger filter-trigger--mobile" id="filter-trigger-btn">
      ${filterTriggerLabel}
    </button>
    <div class="lcars-search-wrapper">
      <input class="lcars-nav__search lcars-input" id="search-input"
             placeholder="Search..." value="${esc(state.searchQuery)}" />
      ${state.searchQuery ? `<button class="lcars-search-clear" id="search-clear-btn" type="button" aria-label="Clear search">&times;</button>` : ""}
    </div>
    <button class="lcars-nav__block lcars-nav__block--violet" id="add-book-btn">
      Add
    </button>
    <button class="lcars-nav__block lcars-nav__block--tan" id="utilities-btn">
      Utils
    </button>
  `;
}

function renderAddNavButtons(): string {
  return `
    <button class="lcars-nav__block lcars-nav__block--orange" id="nav-save-btn">
      Save
    </button>
    <button class="lcars-nav__block lcars-nav__block--blue" id="nav-cancel-btn">
      Cancel
    </button>
  `;
}

function renderDetailNavButtons(): string {
  return `
    <button class="lcars-nav__block lcars-nav__block--orange" id="nav-save-btn">
      Save
    </button>
    <button class="lcars-nav__block lcars-nav__block--blue" id="nav-cancel-btn">
      Cancel
    </button>
    <button class="lcars-nav__block lcars-nav__block--red" id="nav-delete-btn">
      Delete
    </button>
  `;
}

export function renderUtilitiesMenu(): string {
  const signedIn = isSignedIn();

  return `
    <div class="utilities-overlay" id="utilities-overlay">
      <div class="utilities-menu">
        <div class="utilities-menu__header">Utilities</div>
        <button class="utilities-menu__item utilities-menu__item--orange" id="find-covers-btn">Find Missing Covers</button>
        <button class="utilities-menu__item utilities-menu__item--gold" id="export-btn">Export Library</button>
        <button class="utilities-menu__item utilities-menu__item--lavender" id="import-btn">Import Library</button>
        <button class="utilities-menu__item utilities-menu__item--violet" id="goodreads-btn">Goodreads Import</button>
        ${
          signedIn
            ? '<button class="utilities-menu__item utilities-menu__item--red" id="sign-out-btn">Sign Out</button>'
            : ""
        }
        <button class="utilities-menu__item utilities-menu__item--blue" id="utilities-close-btn">Close</button>
      </div>
    </div>
  `;
}
